import { UrlConfig } from 'config';
import { ItemAskModel, ItemBidModel, ItemModel, ListingModel, MarketModel, PaginationModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ItemActiveMarketData, RawItemData, RawItemActiveMarketData } from 'models/Item/types';
import { RawListingData } from 'models/Listing/types';
import { RawMarketData } from 'models/Market/types';
import { ApiResponse } from 'models/Api/ApiResponse';
import { RawPaginationData } from 'models/Pagination/Pagination';
import { AjaxService } from 'services';

export class ItemApiClient {
  public static async getItemById(id: string): Promise<ItemModel> {
    const url = UrlConfig.ITEM.ITEM_BY_ID + `${id}`;
    const response: ApiResponse<RawItemData> = await AjaxService.getData(url);
    const item = new ItemModel(response.data);
    return item;
  }

  public static async getItemMarket(id: string): Promise<MarketModel> {
    const url = UrlConfig.ITEM.ITEM_BY_ID + `${id}/market`;
    const response: ApiResponse<RawMarketData> = await AjaxService.getData(url);
    const market = new MarketModel(response.data);
    return market;
  }

  public static async getItemActiveMarket(id: string): Promise<ItemActiveMarketData> {
    const url = UrlConfig.ITEM.ITEM_BY_ID + `${id}/active-market`;
    const response: ApiResponse<RawItemActiveMarketData> = await AjaxService.getData(url);
    const activeMarket = {
      bids: response.data?.bids.map(rawBid => new ItemBidModel(rawBid)),
      asks: response.data?.asks.map(rawAsk => new ItemAskModel(rawAsk))
    };
    return activeMarket;
  }

  public static async getItemListings(id: string): Promise<ListingModel[]> {
    const url = UrlConfig.ITEM.ITEM_BY_ID + `${id}/listings`;
    const response: ApiResponse<RawListingData[]> = await AjaxService.getData(url);
    const listings = response.data.map(rawListing => new ListingModel(rawListing));
    return listings;
  }

  public static async getItemSuggestions(id: string): Promise<ItemModel[]> {
    const url = UrlConfig.ITEM.ITEM_BY_ID + `${id}/suggestions`;
    const response: ApiResponse<RawItemData[]> = await AjaxService.getData(url);
    const items = response.data.map(rawItem => new ItemModel(rawItem));
    return items;
  }


  public static async searchItemsByQuery(query: string, page: number = 1): Promise<PaginationModel<ItemModel>> {
    let url = UrlConfig.ITEM.ITEM_SEARCH;

    const response: ApiResponse<RawPaginationData<RawItemData>> = await AjaxService.postData(url, {
      "query": query,
      "page": page,
    });
    const paginatedRespose: PaginationModel<RawItemData> = new PaginationModel(response.data);
    const formattedItems = paginatedRespose.data.map((rawItem: RawItemData) => new ItemModel(rawItem));
    const formattedPaginatedResponse: PaginationModel<ItemModel> = new PaginationModel({ ...response.data, data: formattedItems, fetched: true });
    return formattedPaginatedResponse;
  }


  public static async searchItems(query: JSONObject): Promise<PaginationModel<ItemModel>> {
    let url = UrlConfig.ITEM.ITEM_SEARCH;
    const response: ApiResponse<RawPaginationData<RawItemData>> = await AjaxService.postData(url, query);
    const paginatedRespose: PaginationModel<RawItemData> = new PaginationModel(response.data);
    const formattedItems = paginatedRespose.data.map((rawItem: RawItemData) => new ItemModel(rawItem));
    const formattedPaginatedResponse: PaginationModel<ItemModel> = new PaginationModel({ ...response.data, data: formattedItems, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async getAllItems(page: number = 1): Promise<PaginationModel<ItemModel>> {
    let url = UrlConfig.ITEM.ITEM_SEARCH;
    const response: ApiResponse<RawPaginationData<RawItemData>> = await AjaxService.postData(url, {
      "page": page,
      sortBy: "market_summary.total_sales",
			sortType: 'descending'
    });
    const paginatedRespose: PaginationModel<RawItemData> = new PaginationModel(response.data);
    const formattedItems = paginatedRespose.data.map((rawItem: RawItemData) => new ItemModel(rawItem));
    const formattedPaginatedResponse: PaginationModel<ItemModel> = new PaginationModel({ ...response.data, data: formattedItems, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async placeItemBid(id: string, bidData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_PLACE_BID;
    await AjaxService.postData(url, {
      item_id: id,
      ...bidData
    });

    return id;
  }

  public static async placeItemAsk(id: string, askData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_PLACE_ASK;
    await AjaxService.postData(url, {
      item_id: id,
      ...askData
    });

    return id;
  }

  public static async placeItemInquiry(id: string, inquiryData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_PLACE_INQUIRY;
    await AjaxService.postData(url, {
      item_id: id,
      ...inquiryData
    });

    return id;
  }

  public static async deleteItemBid(itemBidId: string, itemId: string): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_BID_MARK_EXPIRE;
    await AjaxService.postData(url, {
      'item_bid_id': itemBidId
    });
    return itemId;
  }

  public static async deleteItemAsk(itemAskId: string, itemId: string): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_ASK_MARK_EXPIRE;
    await AjaxService.postData(url, {
      'item_ask_id': itemAskId
    });
    return itemId;
  }

  public static async updateItemBid(itemBidId: string, itemId: string, bidUpdateData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_BID + `${itemBidId}/update`;
    await AjaxService.putData(url, bidUpdateData);
    return itemId;
  }

  public static async updateItemAsk(itemAskId: string, itemId: string, askUpdateData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_ASK + `${itemAskId}/update`;
    await AjaxService.putData(url, askUpdateData);
    return itemId;
  }

  public static async updateItem(id: string, itemData: JSONObject): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_UPDATE + `${id}/update`;
    await AjaxService.putData(url, itemData);
    return id;
  }

  public static async createItemFromListing(listingId: string): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_CREATE_FROM_LISTING + `${listingId}`;
    const response: ApiResponse<string> = await AjaxService.postData(url);
    return response.data;
  }

  public static async refreshItemMarket(id: string): Promise<void> {
    const url = UrlConfig.ITEM.ITEM_MARKET_REFRESH;
    await AjaxService.postData(url, {
      item_id: id
    });
  }

  public static async enhanceItem(id: string): Promise<void> {
    const url = UrlConfig.ITEM.ITEM_ENHANCE;
    await AjaxService.postData(url, {
      item_id: id
    });
  }

  public static async removeItemBackground(id: string): Promise<void> {
    const url = UrlConfig.ITEM.ITEM_REMOVE_BACKGROUND;
    await AjaxService.postData(url, {
      item_id: id
    });
  }

  public static async demoteItem(id: string, reason: string): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_DEMOTE;
    const response: ApiResponse<string> = await AjaxService.postData(url, {
      "item_id": id,
      "reason": reason
    });
    return response.data;
  }

  public static async migrateItemImage(id: string, imageUrl: string): Promise<void> {
    const url = UrlConfig.ITEM.ITEM_MIGRATE_IMAGE;
    await AjaxService.postData(url, {
      item_id: id,
      image: imageUrl
    });
  }
}

export class DemotedItemApiClient {
  public static async getDemotedItemById(id: string): Promise<ItemModel> {
    const url = UrlConfig.DEMOTED_ITEM.ITEM_BY_ID + `${id}`;
    const response: ApiResponse<RawItemData> = await AjaxService.getData(url);
    const item = new ItemModel(response.data);
    return item;
  }

  public static async searchDemotedItemsByQuery(query: string, page: number = 1): Promise<PaginationModel<ItemModel>> {
    let url = UrlConfig.DEMOTED_ITEM.ITEM_SEARCH;

    const response: ApiResponse<RawPaginationData<RawItemData>> = await AjaxService.postData(url, {
      "query": query,
      "page": page,
    });
    const paginatedRespose: PaginationModel<RawItemData> = new PaginationModel(response.data);
    const formattedItems = paginatedRespose.data.map((rawItem: RawItemData) => new ItemModel(rawItem));
    const formattedPaginatedResponse: PaginationModel<ItemModel> = new PaginationModel({ ...response.data, data: formattedItems, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async promoteItem(id: string, reason: string): Promise<string> {
    const url = UrlConfig.ITEM.ITEM_PROMOTE;
    const response: ApiResponse<string> = await AjaxService.postData(url, {
      "item_id": id,
      "reason": reason
    });
    return response.data;
  }
}
